import React, { FunctionComponent, useRef } from 'react';

import classnames from 'classnames';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import {
    GroupNotificationHistory,
    NotificationHistory,
    NotificationHistoryStatus,
    NotificationType,
} from 'admin/notifications/Notifications.types';
import { useProfile } from 'auth/Auth';
import { ScrollHelper } from 'common/scroll-helper/ScrollHelper';
import { RelativeDateTime } from 'core/util/DateRange';

import 'common/notification-tray/notification-tray-item/NotificationTrayItem.scss';

interface Props {
    tab: string;
    handleStatePath: (notification: NotificationHistory) => void;
    groupedNotificationHistory: GroupNotificationHistory;
    fetchAllNotificationHistories: (tab: string, start: number) => void;
    currentStart: number;
    hasMoreNotifications: boolean;
    handleNotificationCount?: (id: string, status: NotificationHistoryStatus) => void;
}

export const NotificationTrayItem: FunctionComponent<Props> = ({
    tab,
    handleStatePath,
    groupedNotificationHistory,
    fetchAllNotificationHistories,
    currentStart,
    hasMoreNotifications,
    handleNotificationCount,
}) => {
    const { t } = useTranslation('admin/notifications');
    const [{ profile }] = useProfile();
    const notificationRef = useRef<HTMLDivElement | null>(null);

    const allowRead = (type: NotificationType): boolean => {
        return type !== NotificationType.DataCollectionStatus;
    };

    const handleMarkUnread = (id: string, status: NotificationHistoryStatus): void => {
        if (handleNotificationCount) {
            handleNotificationCount(id, status);
        }
    };

    const handleMarkRead = (
        id: string,
        status: NotificationHistoryStatus,
        notification?: NotificationHistory,
    ): void => {
        if (notification) {
            handleStatePath(notification);
        } else if (handleNotificationCount) {
            handleNotificationCount(id, status);
        }
    };

    const markButton = (id: string, status: NotificationHistoryStatus): JSX.Element => {
        const markHandler = (): void => {
            return status === NotificationHistoryStatus.UnRead
                ? handleMarkRead(id, status)
                : handleMarkUnread(id, status);
        };

        return (
            <Button
                variant="link"
                onClick={(e): void => {
                    e.stopPropagation();
                    markHandler();
                }}
            >
                {status === NotificationHistoryStatus.UnRead ? t('MARK_AS_READ') : t('MARK_AS_UNREAD')}
            </Button>
        );
    };

    return (
        <div className="r4e-notification-tray-item">
            <ScrollHelper position="top" direction="top" parent={notificationRef} />
            <InfiniteScroll
                pageStart={0}
                initialLoad={false}
                loadMore={(): void => {
                    fetchAllNotificationHistories(tab, currentStart);
                }}
                hasMore={hasMoreNotifications}
                useWindow={false}
            >
                {Object.keys(groupedNotificationHistory).length === 0 ? (
                    <div className="no-notifications">
                        <i className="icon-check-circle" />
                        {tab === 'all' ? (
                            <div>{t('NO_NOTIFICATIONS')}</div>
                        ) : (
                            <div>{t('NO_UNREAD_NOTIFICATIONS_TRAY')}</div>
                        )}
                    </div>
                ) : (
                    ''
                )}
                <ul>
                    {Object.keys(groupedNotificationHistory).map((key) => {
                        const notifications = groupedNotificationHistory[key];

                        return (
                            <React.Fragment key={key}>
                                <li className="notification-group-by" key={key}>
                                    <div className="notification-group-name">{t(key)}</div>
                                </li>
                                {notifications.map((notification, i) => {
                                    return (
                                        <li key={notification.id.toString()}>
                                            <div
                                                role="button"
                                                tabIndex={i}
                                                onKeyDown={(): void => {
                                                    if (!allowRead(notification.type)) {
                                                        return;
                                                    }
                                                    handleMarkRead(notification.id, notification.status, notification);
                                                }}
                                                onClick={(e): void => {
                                                    if (!allowRead(notification.type)) {
                                                        return;
                                                    }
                                                    handleMarkRead(notification.id, notification.status, notification);
                                                }}
                                                className={classnames(
                                                    'notification-list-item',
                                                    {
                                                        'notification-nonactionable': !allowRead(notification.type),
                                                    },
                                                    {
                                                        'notification-actionable': allowRead(notification.type),
                                                    },
                                                    {
                                                        'notification-unread':
                                                            tab === 'all' &&
                                                            notification.status === NotificationHistoryStatus.UnRead &&
                                                            allowRead(notification.type),
                                                    },
                                                )}
                                            >
                                                <Row noGutters>
                                                    <Col>
                                                        <div className="icon-container">
                                                            <i className={notification.icon} />
                                                        </div>
                                                    </Col>
                                                    <Col sm={10}>
                                                        <div
                                                            className="notification-payload"
                                                            // eslint-disable-next-line react/no-danger
                                                            dangerouslySetInnerHTML={{ __html: notification.payload }}
                                                        />
                                                        <div>
                                                            <span className="notification-time-ago">
                                                                <RelativeDateTime
                                                                    dateTime={notification.sentDate}
                                                                    addTimeAgo
                                                                />
                                                            </span>
                                                            {profile &&
                                                            !profile.isImpersonated &&
                                                            tab === 'all' &&
                                                            allowRead(notification.type)
                                                                ? markButton(notification.id, notification.status)
                                                                : ''}
                                                        </div>
                                                    </Col>
                                                    <Col>
                                                        {allowRead(notification.type) ? (
                                                            <div className="icon-angle-container">
                                                                <i className="icon-angle-right" />
                                                            </div>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </Col>
                                                </Row>
                                            </div>
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </ul>
            </InfiniteScroll>
        </div>
    );
};
